import { type MouseEvent as ReactMouseEvent, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { LegalDocumentModal } from "../components/LegalDocumentModal";
import { type LegalDocumentKind } from "../content/legalDocuments";
import { type SiteStats, type User } from "../services/api";
import { type Language, type MediaKind, type SiteContent } from "../types/siteContent";

type NavItemId = "features" | "pricing" | "download";
type PageSectionId = "features" | "download" | "final-cta";
type ActiveNavItem = NavItemId | null;
type ScrollIntent = { navItem: NavItemId } | null;
export type HomePageAdminPreviewTargetId = string;

// Fallback only. The header contact modal uses the CMS-managed
// `contactQrUrl`; this bundled asset keeps old drafts and local first-boot
// content from showing an empty QR panel.
const INTERNAL_TEST_GROUP_QR_URL = "/wechat-internal-test-qr.jpg";

const NAV_ITEMS: Array<{ id: NavItemId; sectionId: PageSectionId; href: `#${PageSectionId}`; labelKey: "navFeatures" | "navPricing" | "navDownload" }> = [
  { id: "features", sectionId: "features", href: "#features", labelKey: "navFeatures" },
  { id: "pricing", sectionId: "download", href: "#download", labelKey: "navPricing" },
  { id: "download", sectionId: "final-cta", href: "#final-cta", labelKey: "navDownload" }
];

function findNavItem(navItem: NavItemId) {
  return NAV_ITEMS.find((item) => item.id === navItem);
}

const ROLLING_STAT_DURATION_MS = 520;
let rollingStatSlotSeed = 0;

type RollingStatSlot = {
  from: string;
  to: string;
  key: string;
  shouldAnimate: boolean;
  direction: "up" | "down";
  isDigit: boolean;
};

function isStatDigit(character: string) {
  return /^[0-9]$/.test(character);
}

function displayStatCharacter(character: string) {
  return character === " " ? "\u00A0" : character;
}

function makeRollingStatSlots(fromText: string, toText: string, shouldAnimate: boolean): RollingStatSlot[] {
  const length = Math.max(fromText.length, toText.length);
  const fromPadded = fromText.padStart(length, " ");
  const toPadded = toText.padStart(length, " ");

  return Array.from({ length }, (_, index) => {
    const from = fromPadded[index] || " ";
    const to = toPadded[index] || " ";
    const fromIsDigit = isStatDigit(from);
    const toIsDigit = isStatDigit(to);
    const direction = fromIsDigit && toIsDigit && Number(to) < Number(from) ? "down" : "up";

    rollingStatSlotSeed += 1;
    return {
      from,
      to,
      key: `${rollingStatSlotSeed}-${index}-${from}-${to}`,
      shouldAnimate: shouldAnimate && from !== to,
      direction,
      isDigit: fromIsDigit || toIsDigit
    };
  });
}

function RollingStatNumber({ text }: { text: string }) {
  const previousTextRef = useRef(text);
  const [slots, setSlots] = useState(() => makeRollingStatSlots(text, text, false));

  useEffect(() => {
    const previousText = previousTextRef.current;
    if (previousText === text) return undefined;

    setSlots(makeRollingStatSlots(previousText, text, true));
    previousTextRef.current = text;

    const timer = window.setTimeout(() => {
      setSlots(makeRollingStatSlots(text, text, false));
    }, ROLLING_STAT_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [text]);

  return (
    <span className="stat-rolling-number" aria-label={text}>
      {slots.map((slot) => {
        const className = [
          "stat-rolling-digit",
          slot.isDigit ? "is-digit" : "is-symbol",
          slot.shouldAnimate ? "is-changing" : "",
          slot.direction === "down" ? "is-down" : "is-up"
        ].filter(Boolean).join(" ");

        return (
          <span className={className} aria-hidden="true" key={slot.key}>
            {slot.shouldAnimate ? (
              <>
                <span className="stat-rolling-digit-frame stat-rolling-digit-current">{displayStatCharacter(slot.from)}</span>
                <span className="stat-rolling-digit-frame stat-rolling-digit-next">{displayStatCharacter(slot.to)}</span>
              </>
            ) : (
              <span className="stat-rolling-digit-frame">{displayStatCharacter(slot.to)}</span>
            )}
          </span>
        );
      })}
    </span>
  );
}

type Props = {
  language: Language;
  user: User | null;
  authLabel: string;
  downloadMessage: string | null;
  downloadPending?: boolean;
  authIcon: ReactNode;
  logoutIcon: ReactNode;
  content: SiteContent;
  stats: SiteStats | null;
  onLanguageChange: (language: Language) => void;
  onAuthOpen: () => void;
  onAccountOpen: () => void;
  onPricingOpen?: () => void;
  onCanvasOpen?: () => void;
  onLogout: () => void;
  onDownload: () => void;
  adminPreview?: {
    selectedTargetId: HomePageAdminPreviewTargetId | null;
    onSelectTarget: (targetId: HomePageAdminPreviewTargetId) => void;
  };
};

export function HomePage(props: Props) {
  // `copy` is the only homepage copy source. It comes from the published CMS
  // payload loaded by `App`, which means changing text in `/admin` updates the
  // public page after publish without rebuilding frontend source.
  const copy = props.content[props.language];
  const adminPreview = props.adminPreview;
  const heroMediaKind = props.content.assets.heroMediaKind;
  const heroMediaUrl = props.content.assets.heroMediaUrl || props.content.assets.heroImageUrl || props.content.assets.videoUrl;
  const installVideoUrl = props.content.assets.installVideoUrl;
  const installVideoPosterUrl = props.content.assets.installVideoPosterUrl;
  const [activeNavItem, setActiveNavItem] = useState<ActiveNavItem>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [wechatQrOpen, setWechatQrOpen] = useState(false);
  const [wechatQrAvailable, setWechatQrAvailable] = useState(true);
  const [legalModal, setLegalModal] = useState<LegalDocumentKind | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const navRef = useRef<HTMLElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  // A click-triggered smooth scroll needs a temporary intent lock. Without it,
  // the scroll listener can briefly re-activate the section currently under the
  // viewport before the browser reaches the clicked anchor, making the underline
  // animate away and then back again.
  const scrollIntentRef = useRef<ScrollIntent>(null);

  const updateNavIndicator = useCallback(() => {
    const nav = navRef.current;
    if (!nav || !indicatorRef.current) return;

    if (activeNavItem === null) {
      indicatorRef.current.style.opacity = "0";
      return;
    }

    const activeLink = nav.querySelector<HTMLAnchorElement>(`a[data-nav-item="${activeNavItem}"]`);

    if (!activeLink) {
      indicatorRef.current.style.opacity = "0";
      return;
    }

    const navRect = nav.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();
    const x = linkRect.left - navRect.left;

    indicatorRef.current.style.opacity = "1";
    indicatorRef.current.style.width = `${linkRect.width}px`;
    indicatorRef.current.style.transform = `translateX(${x}px)`;
  }, [activeNavItem]);

  const handleNavClick = useCallback((navItem: NavItemId) => {
    const targetItem = findNavItem(navItem);
    scrollIntentRef.current = targetItem ? { navItem: targetItem.id } : null;
    setActiveNavItem(navItem);
  }, []);

  useLayoutEffect(() => {
    updateNavIndicator();
  }, [copy.navDownload, copy.navFeatures, copy.navPricing, props.language, updateNavIndicator]);

  useEffect(() => {
    updateNavIndicator();

    const nav = navRef.current;
    if (!nav) return;

    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      updateNavIndicator();
    });

    if (resizeObserver) {
      resizeObserver.observe(nav);
      nav.querySelectorAll("a").forEach((link) => resizeObserver.observe(link));
    }

    const handleNavMeasure = () => updateNavIndicator();
    const fontReady = "fonts" in document ? document.fonts.ready : Promise.resolve();
    fontReady.then(handleNavMeasure).catch(() => undefined);

    window.addEventListener("resize", handleNavMeasure);
    window.addEventListener("load", handleNavMeasure);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", handleNavMeasure);
      window.removeEventListener("load", handleNavMeasure);
    };
  }, [updateNavIndicator]);

  const freeTrialLabel = copy.downloadNow || (props.language === "en" ? "Free Trial" : "免费试用");
  const internalTestGroupLabel = props.language === "en" ? "Join Beta Group" : "加入内测群";
  const internalTestGroupModalTitle = props.language === "en" ? "Scan to Join the Beta Group" : "扫码加入内测群";
  const internalTestGroupModalCopy = props.language === "en"
    ? "Use WeChat to scan the QR code and get beta updates, trial perks, and release notes."
    : "使用微信扫描二维码，获取内测更新、试用福利和发布通知。";
  const internalTestGroupQrFallback = props.language === "en" ? "QR code pending upload" : "二维码待上传";
  const standardPlan = copy.plans.find((plan) => plan.key.toLowerCase() === "standard");
  const proPlan = copy.plans.find((plan) => plan.key.toLowerCase() === "pro");
  const temporaryFreePlanLines = [
    {
      label: props.language === "en" ? "Standard access" : "Standard 权益",
      value: standardPlan?.lines[0]?.value || (props.language === "en" ? "Unlimited modeling credits during the beta period." : "内测期间开放高频 AI 建模能力。")
    },
    {
      label: props.language === "en" ? "Pro workflow" : "Pro 体验",
      value: proPlan?.lines[1]?.value || (props.language === "en" ? "Try intensive modeling, presentation output, and team pilot workflows." : "体验高强度建模、汇报表达和团队试点工作流。")
    },
    {
      label: props.language === "en" ? "Beta perks" : "内测福利",
      value: props.language === "en" ? "Join the beta group for feature updates and more trial benefits." : "加入内测群获取功能更新、试用名额和更多福利。"
    }
  ];

  useEffect(() => {
    if (!wechatQrOpen && !contactModalOpen) return;

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setWechatQrOpen(false);
        setContactModalOpen(false);
      }
    }

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [contactModalOpen, wechatQrOpen]);

  useEffect(() => {
    if (adminPreview) return;

    // The navigation highlighter is structural behavior rather than content.
    // It stays in source because admins should edit labels/links, not scroll
    // detection rules or section ids.
    // TEMP: FAQ temporarily hidden per user request (2026-06-11).
    // FAQ section content is preserved below, commented out.
    function getScrollActiveNavItem(headerHeight: number): ActiveNavItem {
      const firstSection = document.getElementById("features");
      const viewportTop = headerHeight + 24;
      const viewportBottom = window.innerHeight;

      if (!firstSection || firstSection.getBoundingClientRect().top > viewportTop) {
        return null;
      }

      let activeItem: ActiveNavItem = null;
      let activeScore = 0;

      // Use visible section area instead of a single probe point. This keeps
      // the nav stable when a short final CTA and the footer share the viewport,
      // and avoids the header probe remaining inside the previous section.
      NAV_ITEMS.forEach((item) => {
        const section = document.getElementById(item.sectionId);
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const visibleTop = Math.max(rect.top, viewportTop);
        const visibleBottom = Math.min(rect.bottom, viewportBottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const sectionHeight = Math.max(1, rect.height);
        const visibilityRatio = visibleHeight / sectionHeight;
        const score = visibleHeight + visibilityRatio * 240;

        if (score > activeScore) {
          activeScore = score;
          activeItem = item.id;
        }
      });

      return activeItem;
    }

    function updateActiveNav() {
      const header = document.querySelector<HTMLElement>(".site-header");
      const headerHeight = header ? header.offsetHeight : 0;
      const scrollActiveNavItem = getScrollActiveNavItem(headerHeight);
      const scrollIntent = scrollIntentRef.current;

      // During native smooth scrolling, keep the clicked target highlighted
      // until the viewport scoring confirms that the target section is active.
      if (scrollIntent) {
        if (scrollActiveNavItem !== scrollIntent.navItem) {
          setActiveNavItem(scrollIntent.navItem);
          return;
        }

        scrollIntentRef.current = null;
      }

      setActiveNavItem(scrollActiveNavItem);
    }

    function releaseScrollIntent() {
      // Wheel/touch/key input means the user has taken over scrolling. Drop the
      // click intent immediately so manual scrolling drives the underline.
      scrollIntentRef.current = null;
      updateActiveNav();
    }

    updateActiveNav();
    window.addEventListener("scroll", updateActiveNav, { passive: true });
    window.addEventListener("resize", updateActiveNav);
    window.addEventListener("wheel", releaseScrollIntent, { passive: true });
    window.addEventListener("touchstart", releaseScrollIntent, { passive: true });
    window.addEventListener("keydown", releaseScrollIntent);
    return () => {
      window.removeEventListener("scroll", updateActiveNav);
      window.removeEventListener("resize", updateActiveNav);
      window.removeEventListener("wheel", releaseScrollIntent);
      window.removeEventListener("touchstart", releaseScrollIntent);
      window.removeEventListener("keydown", releaseScrollIntent);
    };
  }, [adminPreview]);

  function selectPreviewTarget(event: ReactMouseEvent<HTMLElement>, targetId: HomePageAdminPreviewTargetId) {
    if (!adminPreview) return false;
    event.preventDefault();
    event.stopPropagation();
    adminPreview.onSelectTarget(targetId);
    return true;
  }

  function editableStateProps(targetId: HomePageAdminPreviewTargetId) {
    if (!adminPreview) return {};
    return {
      "data-admin-editable": "true",
      "data-admin-selected": adminPreview.selectedTargetId === targetId ? "true" : undefined
    };
  }

  function openLegalDocument(event: ReactMouseEvent<HTMLAnchorElement>, kind: LegalDocumentKind) {
    event.preventDefault();
    if (adminPreview) {
      adminPreview.onSelectTarget("footer-meta");
      return;
    }
    event.stopPropagation();
    setLegalModal(kind);
  }

  function featureMedia(index: number, featureMediaKind: MediaKind, mediaUrl: string, legacyImageUrl: string) {
    // Feature media now lives on each feature record as kind + URL. The legacy
    // `imageUrl` and global `featureImageUrls` values remain as image-only
    // fallbacks so older published snapshots still display correctly after the
    // schema expansion. We intentionally do not fall back to Hero media here:
    // Hero is a separate first-screen asset, and using it for empty feature
    // records makes one admin edit appear to replace every homepage media block.
    const legacyFeatureImage = legacyImageUrl || props.content.assets.featureImageUrls[index];
    if (mediaUrl) return { kind: featureMediaKind, url: mediaUrl };
    if (legacyFeatureImage) return { kind: "image" as const, url: legacyFeatureImage };
    return { kind: "image" as const, url: "" };
  }

  function formatHeroStat(value: number | null | undefined) {
    // Stats are fetched asynchronously from `/api/stats`. Rendering a stable
    // placeholder prevents the hero layout from jumping while the backend,
    // database, or local dev server is still starting.
    if (typeof value !== "number" || !Number.isFinite(value)) return "--";
    return new Intl.NumberFormat(props.language === "en" ? "en-US" : "zh-CN").format(value);
  }

  const visibleFeatureEntries = copy.features
    .map((feature, index) => ({ feature, index }))
    .filter(({ feature }) => feature.isVisible)
    .sort((first, second) => first.feature.sortOrder - second.feature.sortOrder);

  function renderFreeTrialDownloadButton() {
    return (
      <button
        className="button button-ai-install"
        type="button"
        disabled={Boolean(props.downloadPending)}
        onClick={(event) => {
          if (selectPreviewTarget(event, "download-button")) return;
          props.onDownload();
        }}
        {...editableStateProps("download-button")}
      >
        {freeTrialLabel}
      </button>
    );
  }

  function openWechatQrModal() {
    setWechatQrAvailable(true);
    setWechatQrOpen(true);
  }

  function openContactModal() {
    setWechatQrAvailable(true);
    setContactModalOpen(true);
  }

  const contactEmailItem = copy.contactItems.find((item) => !item.external);
  const footerEmailHref = contactEmailItem?.url || "mailto:hello@maipo.ai";
  const contactEmailAddress = footerEmailHref.startsWith("mailto:")
    ? footerEmailHref.slice("mailto:".length).split("?")[0]
    : footerEmailHref;
  const contactEmailLabel = contactEmailItem?.label || (props.language === "en" ? `Email: ${contactEmailAddress}` : `邮箱：${contactEmailAddress}`);
  const contactButtonLabel = copy.navContact;
  const contactQrUrl = copy.contactQrUrl || INTERNAL_TEST_GROUP_QR_URL;
  const contactQrAlt = copy.contactQrAlt || (props.language === "en" ? "WeChat QR code" : "微信二维码");
  const betaGroupQrUrl = copy.betaGroupQrUrl || INTERNAL_TEST_GROUP_QR_URL;
  const betaGroupQrAlt = copy.betaGroupQrAlt || (props.language === "en" ? "WeChat beta group QR code" : "微信群二维码");

  return (
    <>
      {!adminPreview ? <div className="fixed-auth-wrapper" ref={userMenuRef}>
        <button
          className="fixed-auth-button"
          type="button"
          aria-label={props.authLabel}
          aria-expanded={props.user ? userMenuOpen : undefined}
          onClick={() => {
            if (props.user) {
              setUserMenuOpen(!userMenuOpen);
            } else {
              props.onAuthOpen();
            }
          }}
        >
          {props.user ? props.authIcon : <span className="fixed-auth-label">Login</span>}
        </button>
        {userMenuOpen ? (
          <div className="user-menu">
            <div className="user-menu-item" onClick={() => { setUserMenuOpen(false); props.onAccountOpen(); }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span>{props.language === "zh" ? "个人主页" : "Profile"}</span>
            </div>
            <div className="user-menu-item" onClick={() => { setUserMenuOpen(false); props.onPricingOpen?.(); }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="12 6 14 10 18 10 15 13 16 18 12 15 8 18 9 13 6 10 10 10"/></svg>
              <span>{props.language === "zh" ? "会员套餐" : "Membership"}</span>
            </div>
            <div className="user-menu-item" onClick={() => { setUserMenuOpen(false); }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="10" width="18" height="14" rx="2"/><path d="M16 6a4 4 0 0 0-8 0v4"/></svg>
              <span>{props.language === "zh" ? "我的钱包" : "Wallet"}</span>
            </div>
            <div className="user-menu-divider" />
            <div className="user-menu-item" onClick={() => { setUserMenuOpen(false); props.onLogout(); }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              <span>{props.language === "zh" ? "退出登录" : "Logout"}</span>
            </div>
          </div>
        ) : null}
      </div> : null}
      <header className="site-header">
        <div className="container">
          <a
            className="brand"
            href="#top"
            aria-label={`${copy.brandName} official site`}
            onClick={(event) => {
              selectPreviewTarget(event, "brand");
            }}
            {...editableStateProps("brand")}
          >
            <span className="brand-name">{copy.brandName}</span>
          </a>
          {/*
            TEMP (2026-06-11): FAQ link removed from nav per user request.
            Replaced with Price (points to #download) and a separate Download link
            (points to #final-cta). Restore FAQ link below when re-enabling FAQ section.
          */}
          <nav className="main-nav" aria-label="Main navigation" ref={navRef}>
            {NAV_ITEMS.map((item) => (
              <a
                className={activeNavItem === item.id ? "active" : ""}
                data-nav-item={item.id}
                href={item.href}
                key={item.id}
                onClick={(event) => {
                  if (selectPreviewTarget(event, `nav:${item.labelKey}`)) return;
                  handleNavClick(item.id);
                }}
                {...editableStateProps(`nav:${item.labelKey}`)}
              >
                {copy[item.labelKey]}
              </a>
            ))}
            <button
              className="main-nav-link main-nav-button"
              type="button"
              onClick={(event) => {
                if (selectPreviewTarget(event, "nav:canvas")) return;
                props.onCanvasOpen?.();
              }}
              {...editableStateProps("nav:canvas")}
            >
              {props.language === "en" ? "Canvas" : "功能画布"}
            </button>
            <span className="nav-indicator" ref={indicatorRef} />
          </nav>
          <div className="header-actions">
            <div className="header-controls">
              <div className="toggle-group" aria-label="Language switch">
                <button type="button" onClick={() => props.onLanguageChange(props.language === "zh" ? "en" : "zh")}>
                  {props.language === "zh" ? "EN" : "中"}
                </button>
              </div>
              <div className="header-contact-wrapper">
                <button
                  className="header-login"
                  type="button"
                  onClick={(event) => {
                    if (selectPreviewTarget(event, "header-contact")) return;
                    openContactModal();
                  }}
                  {...editableStateProps("header-contact")}
                >
                  {contactButtonLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div className="container hero-grid">
            <div className="hero-main-copy" onClick={(event) => selectPreviewTarget(event, "hero-title")} {...editableStateProps("hero-title")}>
              <h1>
                <span className="hero-title-line">{copy.heroTitle1}</span><br />
                <span className="hero-title-line">{copy.heroTitle2}{copy.heroTitle3 ? <> <span className="accent-word">{copy.heroTitle3}</span></> : null}</span>
              </h1>
            </div>
            {/*
              TEMP (2026-06-12): Hero video/visual hidden per user request.
              Restore by uncommenting the block below.
            */}
            {/* <div className="hero-visual" aria-label={copy.heroVisualAriaLabel}>
              <div className="hero-window">
                <MediaFrame kind={heroMediaKind} url={heroMediaUrl} label={copy.heroImageAlt} placeholder={copy.brandMark} placeholderClassName="hero-image-placeholder" />
              </div>
            </div> */}
            <div className="hero-bottom-actions">
              <div className="hero-stats" aria-label={copy.heroStatsAriaLabel} onClick={(event) => selectPreviewTarget(event, "hero-stats")} {...editableStateProps("hero-stats")}>
                <div className="hero-stat">
                  <strong className="hero-stat-number"><RollingStatNumber text={formatHeroStat(props.stats?.downloadCount)} /></strong>
                  <span>{copy.heroDownloadCount}</span>
                </div>
                <div className="hero-stat">
                  <strong className="hero-stat-number"><RollingStatNumber text={formatHeroStat(props.stats?.userCount)} /></strong>
                  <span>{copy.heroUserCount}</span>
                </div>
              </div>
              <div className="hero-actions">
                {/* Keep the CTA visible in both download modes; App.handleDownload
                    decides whether this opens the installer or the launch notice. */}
                {renderFreeTrialDownloadButton()}
                {/*
                  TEMP (launch CTA visibility, 2026-06-15):
                  Hide the "view features" shortcut from the same first-screen
                  button group so the hero presents a single primary action.
                  The features section and navigation anchor stay in place, and
                  this link can be restored by uncommenting it when the homepage
                  CTA set is expanded again.
                  <a className="button button-secondary" href="#features">{copy.viewFeatures}</a>
                */}
              </div>
              {props.downloadMessage ? <p className="inline-message">{props.downloadMessage}</p> : null}
            </div>
          </div>
        </section>

        <section className="section alt" id="features">
          <div className="container">
            <div className="feature-grid">
              {visibleFeatureEntries.map(({ feature, index }, displayIndex) => {
                const isReverse = displayIndex % 2 === 1;
                const resolvedMedia = featureMedia(index, feature.mediaKind, feature.mediaUrl, feature.imageUrl);
                const image = (
                  <div className="feature-image reveal feature-reveal-media">
                    <MediaFrame kind={resolvedMedia.kind} url={resolvedMedia.url} label={feature.imageAlt} placeholder={copy.brandMark} placeholderClassName="feature-media-placeholder" />
                  </div>
                );
                const copyBlock = (
                  <div className="feature-copy reveal feature-reveal-copy">
                    <div className="card-index">{feature.eyebrow}</div>
                    <h3>{feature.title}</h3>
                    <p>{feature.body}</p>
                    {feature.tags.length ? <ul className="tag-list">{feature.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul> : null}
                  </div>
                );

                return (
                  <section
                    className={`feature-split ${isReverse ? "reverse" : ""} ${feature.className}`}
                    key={`${feature.eyebrow}-${index}`}
                    onClick={(event) => selectPreviewTarget(event, `feature:${index}`)}
                    {...editableStateProps(`feature:${index}`)}
                  >
                    {isReverse ? image : copyBlock}
                    {isReverse ? copyBlock : image}
                  </section>
                );
              })}
            </div>
          </div>
        </section>

        <section className="section" id="download">
          <div className="container">
            <div className="section-head reveal" onClick={(event) => selectPreviewTarget(event, "download-section")} {...editableStateProps("download-section")}>
              <h2>{copy.downloadTitle}</h2>
            </div>
            <p className="download-section-copy" onClick={(event) => selectPreviewTarget(event, "download-section")} {...editableStateProps("download-section")}>{copy.downloadCopy}</p>
            <div className="pricing-stack">
              {/* TEMP (pricing campaign, 2026-06-30):
                  This limited-free card is a short-lived internal-test promotion
                  intentionally kept out of CMS plan data so it can be removed
                  without changing the permanent Free / Standard / Pro plans. */}
              <article className="temporary-free-card reveal" aria-label={props.language === "en" ? "Limited free beta card" : "限时免费内测卡片"}>
                <div className="temporary-free-card-copy">
                  <div className="tag">2026.7.18 - 2026.10.18</div>
                  <h3>{props.language === "en" ? "Limited Free" : "限时免费"}</h3>
                  <p>
                    {props.language === "en"
                      ? "During the beta period, start with Standard-level modeling capacity and preview selected Pro workflows before choosing a paid plan."
                      : "内测期间，先免费体验 Standard 级别的高频建模能力，并提前试用部分 Pro 的专业工作流。"}
                  </p>
                  <ul className="temporary-free-list">
                    {temporaryFreePlanLines.map((line) => (
                      <li key={line.label}><strong>{line.label}</strong>{line.value}</li>
                    ))}
                  </ul>
                </div>
                <div className="temporary-free-actions">
                  {renderFreeTrialDownloadButton()}
                  <button className="button button-primary temporary-free-group-link" type="button" onClick={(event) => {
                    if (selectPreviewTarget(event, "download-section")) return;
                    openWechatQrModal();
                  }}>
                    {internalTestGroupLabel}
                  </button>
                </div>
              </article>
              <div className="plan-grid" aria-hidden="true">
                {copy.plans.map((plan, index) => (
                  <article
                    className="plan-card reveal"
                    key={`plan-${index}`}
                    onClick={(event) => selectPreviewTarget(event, `plan:${index}`)}
                    {...editableStateProps(`plan:${index}`)}
                  >
                    <div className="tag">{plan.key}</div>
                    <div className="price">{renderPrice(plan.price)}</div>
                    <p>{plan.body}</p>
                    <ul className="plan-list">
                      {plan.lines.map((line) => (
                        <li key={`${line.label}-${line.value}`}><strong>{line.label}</strong>：{line.value}</li>
                      ))}
                    </ul>
                    <div className="plan-actions">
                      <button className="button button-secondary" type="button" tabIndex={-1} disabled={Boolean(props.downloadPending)} onClick={(event) => {
                        if (selectPreviewTarget(event, `plan:${index}`)) return;
                        props.onDownload();
                      }}>{plan.cta}</button>
                      {index === 0 && renderFreeTrialDownloadButton()}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/*
          TEMP (2026-06-11): FAQ section hidden per user request.
          Content fully preserved below — uncomment to restore.
        {/*
        <section className="section alt" id="faq">
          <div className="container">
            <div className="section-head">
              <div className="eyebrow">{copy.faqSectionEyebrow}</div>
              <h2>{copy.faqTitle}</h2>
            </div>
            <div className="faq-grid">
              {copy.faqs.map((faq) => (
                <article className="faq-card" key={faq.question}>
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        */}

        <section className="final-cta" id="final-cta" onClick={(event) => selectPreviewTarget(event, "final-cta")} {...editableStateProps("final-cta")}>
          <div className="container reveal">
            <h2>{copy.finalTitle}</h2>
            <p>{copy.finalCopy}</p>
            <div className="footer-actions">
              {renderFreeTrialDownloadButton()}
              {/*
                TEMP (launch CTA visibility, 2026-06-15):
                The final CTA also keeps only the Rhino install entry visible.
                The features section remains available through the main
                navigation; uncomment this anchor if the secondary CTA returns.
                <a className="button button-secondary" href="#features">{copy.viewFeatures}</a>
              */}
            </div>
          </div>
        </section>

        {installVideoUrl || adminPreview ? (
          <section className="install-video-section" onClick={(event) => selectPreviewTarget(event, "install-video")} {...editableStateProps("install-video")}>
            <div className="container reveal install-video-layout">
              <div className="install-video-heading">
                <div className="eyebrow">{copy.installVideoEyebrow}</div>
                <h2>{copy.installVideoTitle}</h2>
                <p>{copy.installVideoCopy}</p>
              </div>
              <div className="install-video-frame">
                {installVideoUrl ? (
                  <video
                    aria-label={copy.installVideoLabel}
                    controls
                    playsInline
                    poster={installVideoPosterUrl || undefined}
                    preload="metadata"
                    src={installVideoUrl}
                  />
                ) : (
                  <div className="install-video-placeholder" role="img" aria-label={copy.installVideoLabel}>
                    <span>{copy.installVideoLabel}</span>
                  </div>
                )}
              </div>
            </div>
          </section>
        ) : null}
      </main>

      <footer className="site-footer" id="contact">
        <div className="footer-grid">
          <section className="footer-card reveal" onClick={(event) => selectPreviewTarget(event, "footer-about")} {...editableStateProps("footer-about")}><h3>{copy.aboutTitle}</h3><p>{copy.about}</p></section>
          <section className="footer-card reveal" onClick={(event) => selectPreviewTarget(event, "footer-contact")} {...editableStateProps("footer-contact")}>
            <h3>{copy.contactTitle}</h3>
            <ul className="footer-list">
              {copy.contactItems.filter((item) => item.external).map((item) => {
                const itemIndex = copy.contactItems.indexOf(item);
                return (
                  <li key={`${item.label}-${item.url}`}>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" onClick={(event) => selectPreviewTarget(event, `contact:${itemIndex}`)} {...editableStateProps(`contact:${itemIndex}`)}>{item.label}</a>
                  </li>
                );
              })}
            </ul>
            {copy.contactItems.filter((item) => !item.external).map((item) => {
              const itemIndex = copy.contactItems.indexOf(item);
              return (
                <div className="footer-email-row" key={`${item.label}-${item.url}`}>
                  <a href={item.url} onClick={(event) => selectPreviewTarget(event, `contact:${itemIndex}`)} {...editableStateProps(`contact:${itemIndex}`)}>{item.label}</a>
                </div>
              );
            })}
          </section>
          <div className="footer-meta" onClick={(event) => selectPreviewTarget(event, "footer-meta")} {...editableStateProps("footer-meta")}>
            <div className="footer-meta-group">
              <span>{copy.copyrightText}</span>
            </div>
            <div className="footer-meta-group">
              <span>{copy.footerMeta3}</span>
              <span>{copy.record}</span>
              <span><a className="footer-version-link" href={copy.termsUrl} onClick={(event) => openLegalDocument(event, "terms")}>{copy.terms}</a></span>
              <span><a className="footer-version-link" href={copy.privacyUrl} onClick={(event) => openLegalDocument(event, "privacy")}>{copy.privacy}</a></span>
            </div>
          </div>
        </div>
      </footer>

      {legalModal ? (
        <LegalDocumentModal
          kind={legalModal}
          language={props.language}
          closeLabel={props.language === "en" ? "Close" : "关闭"}
          onClose={() => setLegalModal(null)}
        />
      ) : null}

      {contactModalOpen ? (
        <div className="modal-backdrop contact-modal-backdrop" role="presentation" onClick={() => setContactModalOpen(false)}>
          <section
            className="auth-modal contact-modal"
            role="dialog"
            aria-modal="true"
            aria-label={contactButtonLabel}
            onClick={(event) => event.stopPropagation()}
          >
            <button className="modal-close" type="button" aria-label={props.language === "en" ? "Close" : "关闭"} onClick={() => setContactModalOpen(false)}>×</button>
            <div className="contact-modal-content">
              <div className="contact-qr-panel">
                <span className="contact-modal-label">{props.language === "en" ? "WeChat" : "微信扫码"}</span>
                <div className="wechat-qr-frame contact-qr-frame">
                  {wechatQrAvailable ? (
                    <img
                      src={contactQrUrl}
                      alt={contactQrAlt}
                      onError={() => setWechatQrAvailable(false)}
                    />
                  ) : (
                    <div className="wechat-qr-placeholder">{internalTestGroupQrFallback}</div>
                  )}
                </div>
              </div>
              <a className="contact-email-panel" href={footerEmailHref}>
                <strong>{contactEmailLabel}</strong>
              </a>
            </div>
          </section>
        </div>
      ) : null}

      {wechatQrOpen ? (
        <div className="modal-backdrop wechat-qr-backdrop" role="presentation" onClick={() => setWechatQrOpen(false)}>
          <section
            className="auth-modal wechat-qr-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wechat-qr-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button className="modal-close" type="button" aria-label={props.language === "en" ? "Close" : "关闭"} onClick={() => setWechatQrOpen(false)}>×</button>
            <h2 id="wechat-qr-title">{internalTestGroupModalTitle}</h2>
            <p>{internalTestGroupModalCopy}</p>
            <div className="wechat-qr-frame">
              {wechatQrAvailable ? (
                <img
                  src={betaGroupQrUrl}
                  alt={betaGroupQrAlt}
                  onError={() => setWechatQrAvailable(false)}
                />
              ) : (
                <div className="wechat-qr-placeholder">{internalTestGroupQrFallback}</div>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}

function MediaFrame(props: {
  kind: MediaKind;
  url: string;
  label: string;
  placeholder: string;
  placeholderClassName: string;
}) {
  const [hasTransparency, setHasTransparency] = useState(false);

  useEffect(() => {
    setHasTransparency(false);
  }, [props.kind, props.url]);

  function inspectTransparency(image: HTMLImageElement) {
    try {
      const naturalWidth = image.naturalWidth;
      const naturalHeight = image.naturalHeight;
      if (!naturalWidth || !naturalHeight) return;

      const sampleWidth = Math.min(naturalWidth, 256);
      const sampleHeight = Math.max(1, Math.round((naturalHeight / naturalWidth) * sampleWidth));
      const canvas = document.createElement("canvas");
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;

      context.drawImage(image, 0, 0, sampleWidth, sampleHeight);
      const pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
      for (let index = 3; index < pixels.length; index += 4) {
        if (pixels[index] < 255) {
          setHasTransparency(true);
          return;
        }
      }
      setHasTransparency(false);
    } catch {
      // Cross-origin images without CORS can still render transparency, but the
      // canvas API cannot inspect their pixels. Keep the normal media treatment.
      setHasTransparency(false);
    }
  }

  // A single renderer keeps Hero and Feature media behavior identical. Videos
  // are muted and `playsInline` so browsers can autoplay them inside marketing
  // containers without forcing a fullscreen player on mobile.
  if (!props.url) {
    return <div className={props.placeholderClassName} aria-hidden="true">{props.placeholder}</div>;
  }

  if (props.kind === "video") {
    return (
      <video
        aria-label={props.label}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        src={props.url}
      />
    );
  }

  return <img className={hasTransparency ? "media-has-transparency" : undefined} src={props.url} alt={props.label} onLoad={(event) => inspectTransparency(event.currentTarget)} />;
}

function renderPrice(price: string) {
  // Price is editable marketing copy, not a billing object. Splitting the unit
  // after `/` preserves the existing visual treatment without constraining how
  // admins write currencies or periods.
  const slashIndex = price.indexOf("/");
  if (slashIndex === -1) return price;
  return (
    <>
      {price.slice(0, slashIndex)}
      <span className="price-unit">{price.slice(slashIndex)}</span>
    </>
  );
}
